import React, { useEffect, useRef, useState } from 'react';
import useClickOutsideSentinel from '../../../../utils/hooks/useClickOutsideSentinel';
import { allDice, DiceProduct, styles } from '../DiceInfo';
import { AnimatePresence, motion, stagger, useDragControls, useMotionValue, useMotionValueEvent, useScroll, useTransform } from 'motion/react';
import { animate } from 'motion';
import { chevrons } from '../../../svg/SVGData';

const root = '/pips/photos/';

const SubmenuItem: React.FC<{ children: any, onClick: Function }> = ({ children, onClick }) => {
    const barWidth = useMotionValue('35%');

    return (
        <motion.div
            layout
            className='flex justify-end w-full hover:cursor-pointer select-none capitalize'
            onClick={() => onClick()}
            onHoverStart={() => animate(barWidth, '95%')}
            onHoverEnd={() => animate(barWidth, '35%')}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
        >
            <div>
                {children}
                <div className='w-full flex justify-end'>
                    <motion.div
                        layout
                        className='border-b-2 mt-1 border-slate-700'
                        style={{ width: barWidth }}
                    />
                </div>
            </div>
        </motion.div>
    )
}

const Submenu: React.FC<{ children: any }> = ({ children }) => {
    const listRef = useRef<HTMLDivElement>(null);
    const dragY = useMotionValue(0);
    const { scrollYProgress } = useScroll({ container: listRef });
    const scrollbarRef = useRef(null);
    const controls = useDragControls();

    const barHeight = 48;

    const handleOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!listRef.current) return;
        let ceiling = listRef.current.offsetHeight - barHeight;
        let dy = scrollYProgress.get() * ceiling;
        if (dy < 0) dy = 0;
        if (dy > ceiling) dy = ceiling;
        dragY.set(dy);
    }

    const handleOnDrag = () => {
        if (!listRef.current) return;
        let ceiling = listRef.current.offsetHeight - barHeight;
        let progress = dragY.get() / ceiling;
        if (progress < 0) progress = 0;
        if (progress > 1) progress = 1;
        scrollYProgress.set(progress);
        listRef.current.scrollTop = progress * ceiling;
    }

    useMotionValueEvent(dragY, 'change', () => {
        if (!listRef.current) return;
        let ceiling = listRef.current.offsetHeight - barHeight;
        if (dragY.get() < 0) dragY.set(0);
        if (dragY.get() > ceiling) dragY.set(ceiling)
    })

    return (
        <motion.div
            className='flex bg-white rounded-bl pr-4 pl-3 pb-2 relative z-10'
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0, transition: { delayChildren: stagger(.5) } }}
            exit={{ opacity: 0, x: 20 }}
        >
            <div
                ref={listRef}
                className='flex flex-col max-h-[338px] overflow-y-auto no-scrollbar pointer-events-auto'
                onScroll={handleOnScroll}
            >
                {children}
                <div
                    ref={scrollbarRef}
                    className='absolute right-0 z-10 h-full flex pl-2 hover:cursor-grab'
                    onPointerDown={(e) => { controls.start(e, { snapToCursor: true }) }}
                >
                    <motion.div
                        drag='y'
                        dragControls={controls}
                        dragConstraints={scrollbarRef}
                        dragElastic={0}
                        dragMomentum={false}
                        onDrag={handleOnDrag}
                        className='pl-2 w-1 h-12 bg-gradient-to-br from-purple-800 to-pink-500 ml-1 hover:cursor-grab relative z-20'
                        style={{
                            y: dragY
                        }}
                    />
                </div>
            </div>
        </motion.div>
    )
}

interface TabProps {
    category: string,
    active: boolean,
    onSelect: Function,
    selection: string
}

const ShopTab: React.FC<TabProps> = ({ category, active, onSelect, selection }) => {
    const baseX = useMotionValue(0);
    const modX = useMotionValue(0);
    const tapX = useMotionValue(0);
    const x = useTransform(() => baseX.get() + modX.get() + tapX.get());

    const styling = category === 'Material' ? 'bg-gradient-to-br from-purple-800 to-pink-400' : 'bg-gradient-to-br from-pink-600 to-red-300';

    useEffect(() => {
        animate(baseX, active ? 12 : 0);
    }, [active])

    return (
        <div className='flex flex-col'
            onClick={() => { onSelect(true) }}
        >
            <div className='flex'>
                <motion.div
                    layout
                    className={'flex items-center text-white pt-1 text-3xl text-sun pl-6 italic pr-8 rounded-br-md hover:cursor-pointer relative ' + styling}
                    onHoverStart={() => animate(modX, 4, { duration: .1 })}
                    onHoverEnd={() => { animate(modX, 0, { duration: .1 }); animate(tapX, 0, { duration: .1 }) }}
                    onPointerDown={() => animate(tapX, 6, { duration: .1 })}
                    onPointerUp={() => animate(tapX, 0, { duration: .1 })}
                >
                    <motion.p
                        className='select-none text-nowrap'
                        style={{ x }}
                    >
                        {category}
                    </motion.p>
                    <div className='absolute right-0 rotate-45 -mr-[50%] mt-[25%] w-[50%] h-[200%]' />
                </motion.div>
                <div className='grow flex justify-end'>
                    {selection ? (
                        <div className='flex flex-col items-end mr-2 text-center'>
                            <AnimatePresence mode='popLayout'>
                                <motion.div
                                    className='capitalize text-truncate'
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -16 }}
                                    key={selection}
                                >
                                    {selection}
                                </motion.div>
                                <motion.div
                                    className='h-px border-b-2 border-gray-800 block'
                                    layout
                                    initial={{ width: 0 }}
                                    animate={{ width: 32, transition: { duration: .75, ease: "easeIn" } }}
                                />
                            </AnimatePresence>

                        </div>
                    ) : (
                        <div className='flex justify-end mr-2'>

                            <motion.svg
                                className='h-full'
                                viewBox={chevrons['left'].viewbox}
                                initial={{ x: 0 }}
                                animate={{ x: -15, transition: { duration: .5, repeatType: "loop", repeat: Infinity } }}
                            >
                                <motion.path fill='black' d={chevrons['left'].path} />

                            </motion.svg> Tap for menu
                        </div>

                    )}

                </div>
            </div>

        </div>
    )
}


interface CarouselItemsProps {
    spotlight: boolean,
    product: DiceProduct,
    onSelect: Function
}

const CarouselItem: React.FC<CarouselItemsProps> = ({ spotlight, product, onSelect }) => {
    return (
        <motion.div
            layout
            className='h-4/5 w-full flex justify-center items-center aspect-video shrink-0'
            onClick={() => onSelect()}
        >
            <motion.img
                layout
                src={`${root}${product.imageResource}`}
                className='h-full object-contain rounded-lg'
                animate={spotlight ? { scale: .9 } : { scale: .8 }}
                whileHover={{ scale: .95 }}
            />
        </motion.div>
    )
}

interface CarouselProps {
    serial: string,
    style: string,
    color: string,
    setSerial: Function,
    setStyle: Function,
    setColor?: Function,
    filtered: DiceProduct[]
}

const DRAG_BUFFER = 25;
const Carousel: React.FC<CarouselProps> = ({ serial, style, setSerial, setStyle, filtered }) => {
    const [showPreview, setShowPreview] = useState(false);
    const [colorCursor, setColorCursor] = useState(0);
    const [styleCursor, setStyleCursor] = useState(0);

    const incrementColorCursor = () => {
        let next = (colorCursor + 1) % filtered.length;
        setColorCursor(next);
        setSerial(filtered[next].serial)
    }
    const decrementColorCursor = () => {
        let next = colorCursor - 1;
        if (next < 0) {
            next = filtered.length - 1;
            setSerial(filtered[next].serial);
            return;
        };
        next = next % filtered.length;
        setColorCursor(next);
        setSerial(filtered[next].serial);
    }
    const incrementStyleCursor = () => {
        let next = (styleCursor + 1) % styles.length;
        setStyleCursor(next);
        setStyle(styles[next]);
    }
    const decrementStyleCursor = () => {
        let prev = styleCursor - 1;
        if (prev < 0) {
            prev = styles.length - 1;
            setStyleCursor(prev);
            setStyle(styles[prev])
            return;
        }
        prev = prev % styles.length;
        setStyleCursor(prev);
        setStyle(styles[prev])
    }

    const dragX = useMotionValue(0);
    const handleOnDragEnd = () => {
        const x = dragX.get();
        if (x <= -DRAG_BUFFER) {
            incrementColorCursor();
        } else if (x >= DRAG_BUFFER) {
            decrementColorCursor();
        }
    }

    useEffect(() => setColorCursor(0), [style, styleCursor]);

    return (
        <div className='flex flex-col justify-center grow py-2 select-none relative'>
            <motion.div
                layout
                className='grow flex items-center size'
                drag='x'
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleOnDragEnd}
                animate={{ x: `-${colorCursor * 100}%` }}
                style={{ x: dragX }}
            >
                {filtered.map((p, i) => <CarouselItem key={i} onSelect={() => setColorCursor(i)} product={p} spotlight={colorCursor === i} />)}
            </motion.div>

            {/* Controls / up, down, left, right */}
            <div className='absolute top-0 left-0 w-full flex justify-center'>
                <div onClick={decrementStyleCursor} className='h-12 px-3 py-1 flex justify-center items-center hover:cursor-pointer'>
                    <motion.svg layout viewBox={chevrons['up'].viewbox} className='size-full' whileTap={{ y: -8 }}>
                        <path fill='black' d={chevrons['up'].path} />
                    </motion.svg>
                </div>
            </div>

            <div className='absolute bottom-0 left-0 w-full flex justify-center'>
                <div onClick={incrementStyleCursor} className='h-12 px-3 py-1 flex justify-center items-center hover:cursor-pointer'>
                    <motion.svg layout viewBox={chevrons['down'].viewbox} className='size-full' whileTap={{ y: 8 }}>
                        <path fill='black' d={chevrons['down'].path} />
                    </motion.svg>
                </div>
            </div>

            <div className='absolute top-0 left-0 h-full flex items-center'>
                <div onClick={decrementColorCursor} className='h-12 px-3 py-1 flex justify-center items-center hover:cursor-pointer'>
                    <motion.svg layout viewBox={chevrons['left'].viewbox} className='size-full' whileTap={{ x: -8 }}>
                        <path fill='black' d={chevrons['left'].path} />
                    </motion.svg>
                </div>
            </div>

            <div className='absolute top-0 right-0 h-full flex items-center'>
                <div onClick={incrementColorCursor} className='h-12 px-3 py-1 flex justify-center items-center hover:cursor-pointer'>
                    <motion.svg layout viewBox={chevrons['right'].viewbox} className='size-full' whileTap={{ x: 8 }}>
                        <path fill='black' d={chevrons['right'].path} />
                    </motion.svg>
                </div>
            </div>

            {/* Expand */}
            <motion.div
                className='absolute bottom-0 left-0 z-20 h-12 ml-[15%] mb-4 rounded bg-gradient-to-br from-red-400 to-red-600 opacity-80 flex items-center justify-center p-1  hover:cursor-pointer'
                onClick={() => setShowPreview(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: .8, transition: { duration: .35 } }}
            >
                <svg viewBox="0 0 640 640" className='h-full'>
                    <path fill='white' d='M408 64L552 64C565.3 64 576 74.7 576 88L576 232C576 241.7 570.2 250.5 561.2 254.2C552.2 257.9 541.9 255.9 535 249L496 210L409 297C399.6 306.4 384.4 306.4 375.1 297L343.1 265C333.7 255.6 333.7 240.4 343.1 231.1L430.1 144.1L391.1 105.1C384.2 98.2 382.2 87.9 385.9 78.9C389.6 69.9 398.3 64 408 64zM232 576L88 576C74.7 576 64 565.3 64 552L64 408C64 398.3 69.8 389.5 78.8 385.8C87.8 382.1 98.1 384.2 105 391L144 430L231 343C240.4 333.6 255.6 333.6 264.9 343L296.9 375C306.3 384.4 306.3 399.6 296.9 408.9L209.9 495.9L248.9 534.9C255.8 541.8 257.8 552.1 254.1 561.1C250.4 570.1 241.7 576 232 576z' />
                </svg>
            </motion.div>

            {/* Preview */}

            {showPreview && (
                <div className='fixed top-0 left-0 z-50 size-full flex justify-center items-center bg-black bg-opacity-70' onClick={() => setShowPreview(false)}>
                    <motion.img src={`${root}${allDice[serial].imageResource}`} />
                </div>
            )}
        </div>
    )
}

const PipsProductsGeneral: React.FC = () => {
    const [serial, setSerial] = useState(allDice['mgd6-12001'].serial);
    const [style, setStyle] = useState(allDice['mgd6-12001'].style);
    const [color, setColor] = useState(allDice['mgd6-12001'].color);

    const [stylesOpen, setStylesOpen] = useState(false);
    const [colorsOpen, setColorsOpen] = useState(false);

    const collapseMenus = () => {
        setStylesOpen(false);
        setColorsOpen(false);
    }
    const openStyles = () => {
        setStylesOpen(true);
        setColorsOpen(false);
    }
    const openColors = () => {
        setStylesOpen(false);
        setColorsOpen(true);
    }

    const mainRef = useClickOutsideSentinel(collapseMenus);

    const [filteredByStyle, setFilteredByStyle] = useState<DiceProduct[]>([]);


    useEffect(() => {
        let f: DiceProduct[] = [];
        for (let k in allDice) {
            if (allDice[k].style === style) f.push(allDice[k]);
        }
        setSerial(f[0].serial);
        setColor(f[0].color);

        let d: DiceProduct[] = [];
        for (let k in allDice) {
            if (allDice[k].style === style) d.push(allDice[k])
        }
        setFilteredByStyle(d);
    }, [style])


    useEffect(() => {
        setColor(allDice[serial].color)
    }, [serial])

    return (
        <div className='flex flex-col items-center gap-y-4 pt-6'>
            <h2 className='text-sun text-4xl bg-black bg-opacity-30 rounded-md px-4 py-1 text-white'>ALL DICE</h2>
            <div ref={mainRef} className='flex flex-col items-center h-96 w-72 bg-white rounded-md shadow-2xl bg-opacity-95 overflow-hidden font-sans relative'>

                {/* Sub-menus */}
                <div className='absolute top-0 right-0 z-10 hover:cursor-pointer select-none'>
                    <AnimatePresence mode='wait'>
                        {stylesOpen && (
                            <Submenu key='styles'>
                                {styles.map(s => <SubmenuItem key={s} onClick={() => { setStyle(s); collapseMenus(); }}>{s}</SubmenuItem>)}
                            </Submenu>
                        )}
                        {colorsOpen && (
                            <Submenu key='colors'>
                                {filteredByStyle.map(p => <SubmenuItem key={p.serial} onClick={() => { setColor(p.color); collapseMenus(); }}>{p.color}</SubmenuItem>)}
                            </Submenu>
                        )}
                    </AnimatePresence>
                </div>

                {/* Tabs */}
                <div className='flex flex-col'>
                    <ShopTab category='Material' active={stylesOpen} onSelect={openStyles} selection={style} />
                    <ShopTab category='Color' active={colorsOpen} onSelect={openColors} selection={color} />
                </div>

                <Carousel color={color} serial={serial} setSerial={setSerial} style={style} setStyle={setStyle} filtered={filteredByStyle} />

                {/* Info */}
                <div className='w-full flex flex-col items-center text-lg text-slate-100 bg-gradient-to-br from-sky-600 to-sky-900 py-2'>
                    <p>Size: {allDice[serial].size ? `${allDice[serial].size}mm` : 'Collection'}</p>
                    {/* <div className='grid grid-cols-3 ml-2 text-mono'>
                    <div>{ + 1}</div>
                    <div className='pb-1'>/</div>
                    <div>{stylesToColors[style].length}</div>
                </div> */}
                </div>
            </div>
        </div>
    )
}

export default PipsProductsGeneral;